import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useScheduleIssues } from "../api/use-issues";
import { IssuesCard } from "./issues-card";

interface WizardStepReviewProps {
  scheduleId: string;
  onFinish: () => void;
}

/**
 * Step 3 of the New Schedule wizard. Shows the flat list of issues from
 * the calendar-import and (optional) course-requests preflight stages so
 * the admin can sanity-check before exiting the wizard. Errors are
 * advisory at this point — the scheduler hasn't run yet — so we never
 * block the Finish button on issue counts. We DO block Finish while the
 * issues query is loading or errored — otherwise the user can complete the
 * wizard having never seen the issue list, defeating the step's purpose.
 */
export function WizardStepReview({
  scheduleId,
  onFinish,
}: WizardStepReviewProps) {
  const issuesQuery = useScheduleIssues(scheduleId);

  const issues = issuesQuery.data ?? [];
  const errorCount = issues.filter((i) => i.severity === "Error").length;

  return (
    <div
      data-testid="wizard-step-review"
      className="bg-surface border border-border-subtle rounded-lg p-6 animate-fade-in"
    >
      <h2 className="text-[17px] font-semibold text-white mb-1">Review</h2>
      <p className="text-[13px] text-text-muted mb-5">
        Review the issues before finishing. Errors block the scheduler from
        running; warnings are advisory.
      </p>

      {issuesQuery.isLoading && (
        <div
          data-testid="wizard-step-review-loading"
          className="py-10 flex justify-center"
        >
          <LoadingSpinner size="lg" />
        </div>
      )}

      {issuesQuery.isError && (
        <div
          role="alert"
          data-testid="wizard-step-review-error"
          className="px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-sm text-red-400 mb-4 flex items-center justify-between gap-3"
        >
          <span>
            {issuesQuery.error instanceof Error
              ? issuesQuery.error.message
              : "Failed to load issues."}
          </span>
          <button
            type="button"
            onClick={() => issuesQuery.refetch()}
            data-testid="wizard-step-review-retry"
            className="bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/40 rounded-md px-2.5 py-1 text-xs font-medium cursor-pointer"
          >
            Retry
          </button>
        </div>
      )}

      {!issuesQuery.isLoading && !issuesQuery.isError && (
        <IssuesCard issues={issues} />
      )}

      {errorCount > 0 && (
        <p
          data-testid="wizard-step-review-fix-later"
          className="mt-4 text-xs text-text-faint"
        >
          You can fix these issues later from the schedule page.
        </p>
      )}

      <div className="flex justify-end mt-6">
        <button
          type="button"
          onClick={onFinish}
          disabled={issuesQuery.isLoading || issuesQuery.isError}
          data-testid="wizard-finish"
          className="bg-brand hover:bg-brand-hover text-white border-none rounded-md px-[18px] py-[9px] font-semibold text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Finish
        </button>
      </div>
    </div>
  );
}
